import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { QUERY_KEYS, STALE_TIME } from '@/libs/constants'
import type {
  CursorPaginationParams,
  HookParams,
  LocalPermissions,
  Roles,
  RolesToPermissions,
  RoleToPermissionsPayload,
} from '@/libs/interfaces'
import { rolesApi } from '@/services/api/roles'

export function useRoles({ params, enabled }: HookParams<CursorPaginationParams>) {
  return useQuery<Partial<Roles[]>>({
    queryKey: QUERY_KEYS.roles.list(params),
    queryFn: () => rolesApi.list(params),
    enabled: enabled ?? true,
    staleTime: STALE_TIME['24Hours'], //24 hours
  })
}

export function useRolesCreate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: rolesApi.create,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.roles.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useRolesDelete() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: rolesApi.delete,
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.roles.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useRolesUpdate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: rolesApi.update,
    onSuccess: (_data) => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.roles.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useRolesPermissionMapping({ params, enabled }: HookParams<Partial<Roles>>) {
  return useQuery<LocalPermissions[]>({
    queryKey: QUERY_KEYS.roles.map(params),
    queryFn: () => rolesApi.getRolesToPermissions(params),
    enabled: enabled ?? true,
    // Feeds the live CASL ability (see useAuth.ts) — must never be served stale.
    staleTime: STALE_TIME.Instant,
  })
}

export function useRolesPermissionMappingCreate() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: ({
      params,
      payload,
    }: {
      params: Partial<RolesToPermissions>
      payload: RoleToPermissionsPayload
    }) => rolesApi.createRolesToPermissions(params, payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.roles.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}

export function useRolesPermissionMappingDelete() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  return useMutation({
    mutationFn: rolesApi.deleteRolesToPermissions,
    onSuccess: () => {
      // roles.all covers roles.map(...), which both RolesManager and the
      // logged-in user's own ability (useAuth.ts) read from.
      void queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.roles.all,
      })
    },
    onError: () => {
      toast.error(t('common.error'))
    },
  })
}
